import { parseISO, isValid } from "date-fns";
import { toZonedTime } from "date-fns-tz";

const formatterCache = new Map<string, Intl.DateTimeFormat>();

const parseDateValue = (date: string | number | Date): Date => {
  if (date instanceof Date) return date;
  if (typeof date === "number") return new Date(date);

  let normalized = date.trim().replace(/\s+/, "T");

  // Handle microsecond precision strings (e.g., .644652 -> .644)
  // This completely stops date-fns from blowing up on backend timestamps
  const dotIndex = normalized.indexOf(".");
  if (dotIndex !== -1) {
    const hasZone = /[Z+\-]/.test(normalized.substring(dotIndex));
    const mainPart = normalized.substring(0, dotIndex);
    const subSeconds = normalized
      .substring(dotIndex + 1)
      .split(/[Z+\-]/)[0]
      .slice(0, 3);
    const zonePart = hasZone
      ? normalized.substring(normalized.search(/[Z+\-](?!.*[Z+\-])/))
      : "Z";

    normalized = `${mainPart}.${subSeconds}${zonePart}`;
  } else if (normalized.includes("T") && !/[Z+\-]/.test(normalized)) {
    // If it's a naked date-time without a decimal or a zone, append Z to force UTC
    normalized += "Z";
  }

  return parseISO(normalized);
};

export const formatDate = (
  date: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {},
): string => {
  if (!date) return "";

  const parsedDate = parseDateValue(date);
  if (!isValid(parsedDate)) return "Invalid Date";

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const zonedDate = toZonedTime(parsedDate, timeZone);
  const cacheKey = JSON.stringify({ timeZone, ...options });

  if (!formatterCache.has(cacheKey)) {
    formatterCache.set(
      cacheKey,
      new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone,
        ...options,
      }),
    );
  }

  return formatterCache.get(cacheKey)!.format(zonedDate);
};
